import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, CheckCircle2, XCircle, Repeat, Zap, DollarSign, ArrowRight, Info, PhoneCall, CalendarClock, Sparkles, ClipboardList, ChevronDown, Clock } from "lucide-react";
import { formatLocalDate } from "@/lib/date";
import { toast } from "sonner";

const SUGGESTED_MONTHLY = [
  { amount: 30, tag: "Slow & steady", note: "$30 × 12 = $360 / yr" },
  { amount: 50, tag: "Recommended", note: "Covers annual dues in 7 months" },
  { amount: 100, tag: "Fast-track", note: "Covers annual dues in ~4 months" },
];

export default function PaymentsPage() {
  const [catalog, setCatalog] = useState([]);
  const [subs, setSubs] = useState([]);
  const [myProperty, setMyProperty] = useState(null);
  const [arrangements, setArrangements] = useState([]);
  const [loading, setLoading] = useState(null);
  const [customMonthly, setCustomMonthly] = useState("");
  const [halfSettlementOpen, setHalfSettlementOpen] = useState(false);
  const [showArrangements, setShowArrangements] = useState(false); // mobile-only collapse for discount/arrangement cards

  // Memoized filter — arrangements list can be large; recomputing on every
  // render (search input, dialog open, etc.) is wasted work.
  const activeArrangements = useMemo(
    () => (arrangements || []).filter((a) => a.status === "active"),
    [arrangements],
  );

  const load = async () => {
    const [c, s, mp, arr] = await Promise.all([
      api.get("/payments/catalog").catch(() => ({ data: [] })),
      api.get("/payments/my-subscriptions").catch(() => ({ data: [] })),
      api.get("/portal/my-property").catch(() => ({ data: null })),
      api.get("/arrangements/mine").catch(() => ({ data: [] })),
    ]);
    setCatalog(c.data || []);
    setSubs(s.data || []);
    setMyProperty(mp.data);
    setArrangements(arr.data || []);  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional load-once on mount

  const startCheckout = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin, property_id: myProperty?.id });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start checkout"); setLoading(null); }
  };

  const startPartialCheckout = async (payment_type) => {
    if (!myProperty) return toast.error("No linked home. Please connect your home first from the portal.");
    setLoading(payment_type);
    try {
      const { data } = await api.post("/payments/partial-checkout", {
        property_id: myProperty.id, payment_type, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setLoading(null); }
  };

  const startCustomMonthly = async () => {
    const amt = parseFloat(customMonthly);
    if (!amt || amt < 1) return toast.error("Enter a monthly amount of at least $1");
    setLoading("custom_monthly_autopay");
    try {
      const { data } = await api.post("/payments/custom-monthly-autopay-checkout", {
        amount: amt, origin_url: window.location.origin, property_id: myProperty?.id,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setLoading(null); }
  };

  const cancelSub = async (id) => {
    if (!window.confirm("Cancel this auto-pay at period end?")) return;
    try {
      await api.post(`/payments/cancel-subscription/${id}`);
      toast.success("Auto-pay will end at period end"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const payInstallment = async (arrangement_id, installment_number) => {
    const key = `arr-${arrangement_id}-${installment_number}`;
    setLoading(key);
    try {
      const { data } = await api.post("/payments/arrangement-installment-checkout", {
        arrangement_id, installment_number, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start checkout"); setLoading(null); }
  };

  const balance = myProperty?.balance || 0;
  const oneTime = catalog.filter((c) => c.type !== "recurring");
  const recurring = catalog.filter((c) => c.type === "recurring");

  return (
    <div data-testid="payments-page" className="max-w-6xl mx-auto px-6 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Portal</p>
      <h1 className="font-serif text-4xl text-[#1A2421] mb-2">Pay HOA Dues</h1>
      <p className="text-[#5C6A64] mb-8 max-w-2xl">
        {balance > 0
          ? <>Your outstanding balance is <span className="font-serif text-[#C86650] text-lg">${balance.toFixed(2)}</span>. Choose how you'd like to pay.</>
          : "Set up auto-pay or pay a specific assessment below."}
      </p>

      {/* Active subscriptions */}
      {subs.length > 0 && (
        <div className="mb-8">
          <h2 className="font-serif text-2xl text-[#1A2421] mb-4 flex items-center gap-2">
            <Repeat className="w-5 h-5 text-[#2C4C3B]" /> Your active auto-pay
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {subs.map((s) => (
              <Card key={s.id} data-testid={`active-sub-${s.id}`} className="rounded-2xl border-[#D5CFC4] bg-[#EADCC8]">
                <CardContent className="p-6 flex justify-between items-center gap-4">
                  <div>
                    <p className="font-serif text-xl text-[#1A2421]">{s.lookup_key.replace(/_/g, " ")}</p>
                    <p className="text-sm text-[#5C6A64] mt-1">${(s.amount / 100).toFixed(2)} · next {new Date(s.current_period_end * 1000).toLocaleDateString()}</p>
                    {s.cancel_at_period_end && <Badge className="mt-2 bg-[#C86650]">Ending at period end</Badge>}
                  </div>
                  {!s.cancel_at_period_end && (
                    <Button variant="outline" size="sm" onClick={() => cancelSub(s.id)} data-testid={`cancel-sub-${s.id}`} className="rounded-full border-[#C86650] text-[#C86650]">
                      Cancel
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Active payment arrangements */}
      {arrangements.filter((a) => a.status === "active").length > 0 && (
        <div className="mb-8" data-testid="my-arrangements">
          <h2 className="font-serif text-2xl text-[#1A2421] mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#C86650]" /> Your payment arrangements
          </h2>
          <div className="space-y-4">
            {activeArrangements.map((a) => {
              const sched = a.schedule || [];
              const paidCount = sched.filter((s) => s.status === "paid").length;
              const paidAmount = sched.filter((s) => s.status === "paid").reduce((sum, s) => sum + (s.amount || 0), 0);
              const nextUnpaid = sched.find((s) => s.status !== "paid");
              const todayIso = new Date().toISOString().slice(0, 10);
              const overdue = nextUnpaid && nextUnpaid.due_date < todayIso;
              return (
                <Card key={a.id} data-testid={`arrangement-${a.id}`} className="rounded-2xl border-2 border-[#C86650]/40 bg-[#FDF9F1]">
                  <CardContent className="p-6">
                    <div className="flex flex-wrap justify-between items-start gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-[#C86650] mb-1">
                          {a.settlement_option === "option2_50_payment_plan" ? "Settlement Plan · Pay 50% of dues" : "Payment plan"}
                        </p>
                        <p className="font-serif text-xl text-[#1A2421]">
                          ${paidAmount.toFixed(2)} paid of ${(a.total_amount || 0).toFixed(2)}
                        </p>
                        <p className="text-sm text-[#5C6A64] mt-1">
                          {paidCount} of {sched.length} installments · {a.frequency || "monthly"}
                        </p>
                      </div>
                      {nextUnpaid && (
                        <div className={`text-right ${overdue ? "text-[#C86650]" : "text-[#2C4C3B]"}`}>
                          <p className="text-xs uppercase tracking-wider">{overdue ? "Overdue" : "Next payment"}</p>
                          <p className="font-serif text-3xl mt-1">${(nextUnpaid.amount || 0).toFixed(2)}</p>
                          <p className="text-xs">Due {formatLocalDate(nextUnpaid.due_date, { month: "short", day: "numeric", year: "numeric" })}</p>
                          <Button
                            data-testid={`pay-next-installment-${a.id}`}
                            onClick={() => payInstallment(a.id, nextUnpaid.installment_number)}
                            disabled={loading === `arr-${a.id}-${nextUnpaid.installment_number}`}
                            className={`mt-2 rounded-full h-9 px-4 ${overdue ? "bg-[#C86650] hover:bg-[#B4553F]" : "bg-[#2C4C3B] hover:bg-[#233D2F]"} text-white`}
                          >
                            {loading === `arr-${a.id}-${nextUnpaid.installment_number}` ? "Redirecting…" : `Pay $${(nextUnpaid.amount || 0).toFixed(2)} now`}
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 w-full h-2 bg-[#EADCC8] rounded-full overflow-hidden">
                      <div className="h-full bg-[#2C4C3B]" style={{ width: `${sched.length ? (paidCount / sched.length) * 100 : 0}%` }} />
                    </div>
                    <div className="mt-4 max-h-52 overflow-y-auto border border-[#EADCC8] rounded-xl">
                      <table className="w-full text-sm">
                        <tbody>
                          {sched.map((s) => {
                            const isPaid = s.status === "paid";
                            const isOverdue = !isPaid && s.due_date < todayIso;
                            const key = `arr-${a.id}-${s.installment_number}`;
                            return (
                              <tr key={`${a.id}-${s.installment_number}`} className="border-b border-[#EADCC8] last:border-0" data-testid={`install-row-${a.id}-${s.installment_number}`}>
                                <td className="px-3 py-2 text-[#5C6A64]">#{s.installment_number}</td>
                                <td className="px-3 py-2 text-[#1A2421]">{formatLocalDate(s.due_date, { month: "short", day: "numeric", year: "numeric" })}</td>
                                <td className="px-3 py-2 text-right font-medium">${(s.amount || 0).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right">
                                  {isPaid
                                    ? <Badge className="bg-[#2C4C3B] text-white text-[10px]">Paid</Badge>
                                    : (isOverdue
                                        ? <Badge className="bg-[#C86650] text-white text-[10px]">Overdue</Badge>
                                        : <Badge variant="outline" className="text-[10px]">Upcoming</Badge>)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {!isPaid && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      data-testid={`pay-installment-${a.id}-${s.installment_number}`}
                                      onClick={() => payInstallment(a.id, s.installment_number)}
                                      disabled={loading === key}
                                      className="rounded-full h-7 px-3 text-xs border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white"
                                    >
                                      {loading === key ? "…" : "Pay"}
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2 items-center pt-3 border-t border-[#EADCC8]">
                      <p className="text-xs text-[#5C6A64] flex-1">
                        Each Stripe payment above will automatically mark the installment paid on your plan.
                      </p>
                      <a href="mailto:saddleridgesavoytrailshoa@gmail.com?subject=Payment%20arrangement%20question" className="text-xs text-[#C86650] underline">Contact the board</a>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Percentage / custom-amount cards — for members with a balance */}
      {myProperty && balance > 0 && (
        <div className="mb-10" data-testid="payment-options-step">
          <h2 className="font-serif text-2xl text-[#1A2421] mb-1">Pay a portion of your balance</h2>
          <p className="text-[#5C6A64] mb-6 max-w-2xl">
            Your outstanding balance is <span className="font-serif text-[#C86650] text-lg">${balance.toFixed(2)}</span>. Choose your payment option:
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(() => {
              const allOptions = [
                { key: "full", payPct: 100, topLabel: "100% of balance", label: "Pay in Full", desc: "Pay the total assessment balance.", alwaysShow: true, isArrangement: false },
                { key: "sixty_five", payPct: 35, topLabel: "65% off balance", label: "65% Off Settlement", desc: "Save 65% — settle in full today for 35% of your balance.", alwaysShow: false, isArrangement: true },
                { key: "half", payPct: 50, topLabel: "50% off balance", label: "50% Off Settlement Plan", desc: "Save 50% — settle over 2–12 monthly installments (max 1 year). Requires board approval.", alwaysShow: false, isArrangement: true },
              ];
              const visible = balance > 500 ? allOptions : allOptions.filter((o) => o.alwaysShow);
              return visible.map((opt) => (
                <Card
                  key={opt.key}
                  data-testid={`option-${opt.key}`}
                  className={`rounded-2xl border-[#D5CFC4] cursor-pointer hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] transition-all duration-300 ${opt.key === "full" ? "border-2 border-[#2C4C3B]" : ""} ${opt.key === "sixty_five" || opt.key === "half" ? "border-2 border-[#C86650]" : ""} ${opt.isArrangement && !showArrangements ? "hidden md:block" : ""}`}
                >
                  <CardContent className="p-6">
                    <div className={`w-11 h-11 rounded-full ${opt.key === "sixty_five" || opt.key === "half" ? "bg-[#C86650]" : "bg-[#EADCC8]"} flex items-center justify-center mb-4`}>
                      <DollarSign className={`w-5 h-5 ${opt.key === "sixty_five" || opt.key === "half" ? "text-white" : "text-[#2C4C3B]"}`} />
                    </div>
                    <p className={`text-xs uppercase tracking-wider ${opt.key === "sixty_five" || opt.key === "half" ? "text-[#C86650] font-medium" : "text-[#5C6A64]"}`}>{opt.topLabel}</p>
                    <p className="font-serif text-2xl text-[#1A2421] mt-1 mb-2">{opt.label}</p>
                    <p className="font-serif text-4xl text-[#2C4C3B] mb-3">${(balance * (opt.payPct / 100)).toFixed(2)}</p>
                    <p className="text-sm text-[#5C6A64] mb-5 leading-relaxed min-h-[3rem]">{opt.desc}</p>
                    <Button
                      data-testid={`select-${opt.key}`}
                      onClick={() => opt.key === "half" ? setHalfSettlementOpen(true) : startPartialCheckout(opt.key)}
                      disabled={loading === opt.key}
                      className={`w-full rounded-full ${opt.key === "full" ? "bg-[#C86650] hover:bg-[#B4553F]" : opt.key === "sixty_five" || opt.key === "half" ? "bg-[#C86650] hover:bg-[#B4553F]" : "bg-[#2C4C3B] hover:bg-[#233D2F]"} text-white h-11`}
                    >
                      {loading === opt.key ? "Redirecting…" : opt.key === "half" ? "Set up plan" : "Continue"} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ));
            })()}

            <CustomAmountCard
              balance={balance}
              loading={loading === "custom"}
              onSubmit={(amt) => {
                if (!myProperty) return toast.error("No linked home.");
                setLoading("custom");
                api.post("/payments/partial-checkout", {
                  property_id: myProperty.id, payment_type: "custom", custom_amount: parseFloat(amt), origin_url: window.location.origin,
                }).then(({ data }) => { window.location.href = data.checkout_url; })
                  .catch((e) => { toast.error(e.response?.data?.detail || "Failed"); setLoading(null); });
              }}
            />

            {/* Mobile-only collapsible toggle — inserted between Custom card and the arrangement cards
                so on small screens homeowners see Pay-Full + Custom by default, then tap to reveal
                the discount/settlement/board-approval options. Hidden entirely on md+. */}
            {balance > 500 && (
              <Button
                data-testid="toggle-arrangements-mobile"
                variant="outline"
                onClick={() => setShowArrangements((v) => !v)}
                className="md:hidden col-span-2 rounded-full border-2 border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white h-12 font-medium"
              >
                {showArrangements ? "Hide payment arrangements" : "Explore payment arrangements"}
                <ChevronDown className={`ml-2 w-4 h-4 transition-transform ${showArrangements ? "rotate-180" : ""}`} />
              </Button>
            )}

            <div className={`${!showArrangements ? "hidden md:contents" : "contents"}`}>
              <ProposePlanDialog myProperty={myProperty} />
              <RequestContactDialog myProperty={myProperty} />
            </div>
          </div>
          {balance <= 500 && (
            <p className="text-xs text-[#5C6A64] mt-4">
              Discount payment options (65% / 50%) are available for balances over $500. Please contact the board if you'd like to discuss a payment plan.
            </p>
          )}
        </div>
      )}

      <HalfSettlementDialog
        open={halfSettlementOpen}
        onOpenChange={setHalfSettlementOpen}
        balance={balance}
        myProperty={myProperty}
        onSubmitted={() => setHalfSettlementOpen(false)}
      />
      {/* Featured 3 payment options */}
      <div className="mb-4">
        <h2 className="font-serif text-2xl text-[#1A2421] mb-1">Featured payment options</h2>
        <p className="text-sm text-[#5C6A64]">Quick single-payment picks — yearly dues, monthly auto-pay, or pay ahead for next year.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mb-10" data-testid="payment-options">
        {/* Option 1 — Yearly Dues */}
        <Card data-testid="option-yearly" className="rounded-2xl border-2 border-[#C86650] bg-[#FDF9F1] hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] transition-all duration-300">
          <CardContent className="p-8">
            <div className="w-11 h-11 rounded-full bg-[#C86650] flex items-center justify-center mb-4">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <Badge className="mb-2 bg-[#C86650]">Most popular</Badge>
            <p className="font-serif text-2xl text-[#1A2421] mb-2">Pay Yearly Dues</p>
            <p className="font-serif text-4xl text-[#2C4C3B] mb-2">$350.00</p>
            <p className="text-sm text-[#5C6A64] mb-6">Pay the current-year annual HOA assessment in a single payment.</p>
            <Button
              data-testid="pay-yearly-btn"
              onClick={() => startCheckout("hoa_annual")}
              disabled={loading === "hoa_annual"}
              className="w-full rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white h-11"
            >
              {loading === "hoa_annual" ? "Redirecting…" : "Pay $350 with Stripe"} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Option 2 — Custom Monthly Auto-Pay with Suggested chips */}
        <Card data-testid="option-custom-monthly" className="rounded-2xl border-2 border-[#2C4C3B] hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] transition-all duration-300">
          <CardContent className="p-8">
            <div className="w-11 h-11 rounded-full bg-[#2C4C3B] flex items-center justify-center mb-4">
              <Repeat className="w-5 h-5 text-white" />
            </div>
            <Badge className="mb-2 bg-[#2C4C3B]">Auto-pay</Badge>
            <p className="font-serif text-2xl text-[#1A2421] mb-1">Custom Monthly Auto-Pay</p>
            <p className="text-sm text-[#5C6A64] mb-3">Choose your own monthly amount. Charged automatically every month; cancel anytime.</p>

            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Suggested amounts</label>
            <div className="flex flex-wrap gap-2 mt-1 mb-3" data-testid="suggested-chips">
              {SUGGESTED_MONTHLY.map((s) => {
                const active = String(s.amount) === customMonthly;
                return (
                  <button
                    key={s.amount}
                    type="button"
                    data-testid={`suggested-chip-${s.amount}`}
                    onClick={() => setCustomMonthly(String(s.amount))}
                    className={`rounded-full px-3 h-9 text-xs font-medium border transition-all ${active ? "bg-[#2C4C3B] text-white border-[#2C4C3B]" : "bg-white text-[#2C4C3B] border-[#D5CFC4] hover:border-[#2C4C3B]"}`}
                    title={s.note}
                  >
                    ${s.amount}/mo
                  </button>
                );
              })}
            </div>
            {(() => {
              const match = SUGGESTED_MONTHLY.find((s) => String(s.amount) === customMonthly);
              return match ? (
                <p className="text-[11px] text-[#2C4C3B] -mt-1 mb-3">
                  <b>{match.tag}:</b> {match.note}
                </p>
              ) : null;
            })()}

            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Monthly amount ($)</label>
            <Input
              data-testid="custom-monthly-input"
              type="number"
              step="0.01"
              min="1"
              value={customMonthly}
              onChange={(e) => setCustomMonthly(e.target.value)}
              placeholder="e.g. 50.00"
              className="mt-1 mb-4 rounded-xl font-serif text-2xl h-14 text-[#2C4C3B]"
            />
            <Button
              data-testid="setup-custom-monthly-btn"
              onClick={startCustomMonthly}
              disabled={loading === "custom_monthly_autopay"}
              className="w-full rounded-full bg-[#2C4C3B] hover:bg-[#233D2F] text-white h-11"
            >
              {loading === "custom_monthly_autopay" ? "Redirecting…" : "Set up auto-pay"} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Option 3 — 2027 Annual Dues */}
        <Card data-testid="option-2027" className="rounded-2xl border-[#D5CFC4] hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] transition-all duration-300">
          <CardContent className="p-8">
            <div className="w-11 h-11 rounded-full bg-[#EADCC8] flex items-center justify-center mb-4">
              <Sparkles className="w-5 h-5 text-[#2C4C3B]" />
            </div>
            <Badge className="mb-2 bg-[#EADCC8] text-[#2C4C3B]">Get ahead</Badge>
            <p className="font-serif text-2xl text-[#1A2421] mb-2">Pay 2027 Annual Dues</p>
            <p className="font-serif text-4xl text-[#2C4C3B] mb-2">$350.00</p>
            <p className="text-sm text-[#5C6A64] mb-6">Pay next year's assessment in advance and stay ahead of the schedule.</p>
            <Button
              data-testid="pay-2027-btn"
              onClick={() => startCheckout("hoa_2027_annual")}
              disabled={loading === "hoa_2027_annual"}
              className="w-full rounded-full bg-[#2C4C3B] hover:bg-[#233D2F] text-white h-11"
            >
              {loading === "hoa_2027_annual" ? "Redirecting…" : "Pay $350 with Stripe"} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Preset plans tabs removed per admin — homeowners no longer see a generic
          "All payment plans" catalog. Real plans are surfaced above (settlement /
          featured cards) or via the board contact form below. */}

      <div className="mt-8 p-5 bg-[#EADCC8] border border-[#D5CFC4] rounded-2xl">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[#2C4C3B] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#1A2421]">
            <strong>Need a different arrangement?</strong> Payment arrangements may be available for balances over $500. <a href="mailto:saddleridgesavoytrailshoa@gmail.com" className="text-[#C86650] underline">Contact the board</a> to discuss a plan or settlement offer.
          </p>
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link to="/portal" className="text-sm text-[#5C6A64] hover:text-[#2C4C3B]" data-testid="pay-back">← Back to portal</Link>
      </div>
    </div>
  );
}

function HalfSettlementDialog({ open, onOpenChange, balance, myProperty, onSubmitted }) {
  const settled = balance * 0.5;
  const [installments, setInstallments] = useState(6);
  const [firstDue, setFirstDue] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const monthly = installments > 0 ? settled / installments : 0;
  const isoTomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const schedulePreview = useMemo(() => {
    if (!firstDue || installments < 2) return [];
    const rows = [];
    const [y, m, d] = firstDue.split("-").map(Number);
    for (let i = 0; i < installments; i++) {
      const dt = new Date(y, m - 1 + i, d);
      rows.push({
        n: i + 1,
        due_date: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`,
        amount: monthly,
      });
    }
    return rows;
  }, [firstDue, installments, monthly]);

  const submit = async () => {
    if (!myProperty) return toast.error("No linked home.");
    if (installments < 2 || installments > 12) return toast.error("Choose 2–12 monthly installments.");
    if (!firstDue) return toast.error("Please pick a first due date.");
    setBusy(true);
    try {
      await api.post("/portal/arrangement-request", {
        kind: "propose_plan",
        settlement_kind: "option2_50_payment_plan",
        installments,
        monthly_amount: parseFloat(monthly.toFixed(2)),
        first_due_date: firstDue,
        deposit_amount: 0,
        address: myProperty.address,
        unit_number: myProperty.unit_number,
        preferred_contact: "email",
        notes: notes || null,
      });
      toast.success("Settlement plan proposal sent to the board for approval.");
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send proposal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="half-settlement-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1A2421]">50% Off Settlement Plan</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-4 bg-[#FDF4F1] border-2 border-[#C86650] rounded-xl">
            <div className="flex items-baseline justify-between mb-1">
              <p className="text-xs uppercase tracking-wider text-[#5C6A64]">Current balance</p>
              <p className="font-serif text-lg text-[#1A2421]">${balance.toFixed(2)}</p>
            </div>
            <div className="flex items-baseline justify-between mb-1">
              <p className="text-xs uppercase tracking-wider text-[#5C6A64]">50% settlement discount</p>
              <p className="font-serif text-lg text-[#2C4C3B]">− ${(balance * 0.5).toFixed(2)}</p>
            </div>
            <div className="flex items-baseline justify-between pt-2 border-t border-[#C86650]/40">
              <p className="text-xs uppercase tracking-wider text-[#C86650] font-medium">Settled total to pay</p>
              <p className="font-serif text-2xl text-[#C86650]">${settled.toFixed(2)}</p>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]"># of monthly installments (2–12)</label>
            <Select value={String(installments)} onValueChange={(v) => setInstallments(parseInt(v, 10))}>
              <SelectTrigger data-testid="half-installments" className="mt-2 rounded-xl h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} months · ${(settled / n).toFixed(2)}/mo</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#5C6A64] mt-1">Payments cannot exceed 12 months (1 year).</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">First payment due date</label>
            <Input
              data-testid="half-first-due"
              type="date"
              min={isoTomorrow}
              value={firstDue}
              onChange={(e) => setFirstDue(e.target.value)}
              className="mt-2 rounded-xl h-11"
            />
          </div>

          {schedulePreview.length > 0 && (
            <div className="p-3 bg-[#FDF9F1] border border-[#EADCC8] rounded-xl max-h-48 overflow-y-auto">
              <p className="text-xs uppercase tracking-wider text-[#5C6A64] mb-2">Schedule preview</p>
              <div className="space-y-1 text-sm">
                {schedulePreview.map((row) => (
                  <div key={row.n} className="flex justify-between">
                    <span>#{row.n} · {formatLocalDate(row.due_date)}</span>
                    <span className="font-medium">${row.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes to the board (optional)</label>
            <Textarea
              data-testid="half-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="Anything the board should know…"
            />
          </div>

          <p className="text-[11px] text-[#5C6A64]">
            Submitting this proposal sends it to the board for approval. Once approved, the 50% discount will be applied and your installment plan will be created. Missed installments may void the settlement offer.
          </p>

          <Button
            data-testid="half-submit"
            onClick={submit}
            disabled={busy}
            className="w-full rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white h-11"
          >
            {busy ? "Sending…" : "Submit settlement proposal"} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CustomAmountCard({ balance, loading, onSubmit }) {
  const [amount, setAmount] = useState("");
  const parsed = parseFloat(amount);
  const valid = !isNaN(parsed) && parsed > 0 && parsed <= balance + 0.01;
  return (
    <Card
      data-testid="option-custom"
      className="rounded-2xl border-2 border-dashed border-[#C86650] hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] transition-all duration-300 bg-[#FDF9F1]"
    >
      <CardContent className="p-6">
        <div className="w-11 h-11 rounded-full bg-[#C86650] flex items-center justify-center mb-4">
          <DollarSign className="w-5 h-5 text-white" />
        </div>
        <p className="text-xs uppercase tracking-wider text-[#C86650]">Your choice</p>
        <p className="font-serif text-2xl text-[#1A2421] mt-1 mb-2">Pay Another Amount</p>
        <div className="mb-3">
          <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Enter amount ($)</label>
          <Input
            data-testid="custom-amount-input"
            type="number"
            step="0.01"
            min="1"
            max={balance.toFixed(2)}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Max $${balance.toFixed(2)}`}
            className="mt-1 rounded-xl font-serif text-2xl h-14 text-[#2C4C3B]"
          />
          <p className="text-[11px] text-[#5C6A64] mt-1">
            Any amount up to your balance of <b>${balance.toFixed(2)}</b>. This is <b>not</b> a payment plan — the remaining balance stays open until paid.
          </p>
        </div>
        <Button
          data-testid="select-custom"
          onClick={() => onSubmit(amount)}
          disabled={loading || !valid}
          className="w-full rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white h-11"
        >
          {loading ? "Redirecting…" : (valid ? `Pay $${parsed.toFixed(2)}` : "Enter an amount")} <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

function ProposePlanDialog({ myProperty }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    deposit_amount: "",
    monthly_amount: "",
    first_due_date: new Date(Date.now() + 21 * 86400 * 1000).toISOString().slice(0, 10),
    phone: "",
    preferred_contact: "email",
    notes: "",
  });

  const submit = async () => {
    if (!form.deposit_amount || !form.monthly_amount || !form.first_due_date) {
      return toast.error("Deposit, monthly amount, and first due date are all required");
    }
    setBusy(true);
    try {
      await api.post("/portal/arrangement-request", {
        kind: "propose_plan",
        deposit_amount: parseFloat(form.deposit_amount),
        monthly_amount: parseFloat(form.monthly_amount),
        first_due_date: form.first_due_date,
        phone: form.phone,
        preferred_contact: form.preferred_contact,
        notes: form.notes,
        address: myProperty?.address,
        unit_number: myProperty?.unit_number,
      });
      toast.success("Your proposed plan was sent to the HOA board for approval.");
      setOpen(false);
      setForm({ ...form, deposit_amount: "", monthly_amount: "", phone: "", notes: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send request");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Card
          data-testid="propose-plan-card"
          className="rounded-2xl border-2 border-[#2C4C3B] cursor-pointer hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] transition-all duration-300"
        >
          <CardContent className="p-8">
            <div className="w-11 h-11 rounded-full bg-[#2C4C3B] flex items-center justify-center mb-4">
              <CalendarClock className="w-5 h-5 text-white" />
            </div>
            <Badge className="mb-2 bg-[#2C4C3B]">Board approval required</Badge>
            <p className="font-serif text-xl text-[#1A2421] mb-2">Pay deposit + monthly plan</p>
            <p className="text-sm text-[#5C6A64] mt-2 mb-6">
              Propose an initial deposit and a monthly amount. The board reviews and, if approved, sets up the plan on your account and sends a payment link for the deposit.
            </p>
            <Button data-testid="propose-plan-btn" className="w-full rounded-full bg-[#2C4C3B] hover:bg-[#233D2F] text-white h-11">
              Propose a plan <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Propose a Payment Plan</DialogTitle></DialogHeader>
        <p className="text-sm text-[#5C6A64] -mt-2 mb-2">The HOA board will review your proposal within 2 business days. No charges are collected until the board approves and sends you a deposit payment link.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Deposit amount ($)</label>
              <Input data-testid="propose-deposit" type="number" step="0.01" min="1" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Monthly ($)</label>
              <Input data-testid="propose-monthly" type="number" step="0.01" min="1" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Proposed first due date</label>
            <Input data-testid="propose-first-due" type="date" value={form.first_due_date} onChange={(e) => setForm({ ...form, first_due_date: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Phone (optional)</label>
              <Input data-testid="propose-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Preferred contact</label>
              <Select value={form.preferred_contact} onValueChange={(v) => setForm({ ...form, preferred_contact: v })}>
                <SelectTrigger data-testid="propose-contact-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone call</SelectItem>
                  <SelectItem value="text">Text message</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes for the board</label>
            <Textarea data-testid="propose-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" placeholder="Any context that will help the board review your request…" />
          </div>
          <Button data-testid="propose-submit" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B] h-11">
            {busy ? "Submitting…" : "Submit for board approval"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RequestContactDialog({ myProperty }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ phone: "", preferred_contact: "phone", notes: "" });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/portal/arrangement-request", {
        kind: "contact_me",
        phone: form.phone,
        preferred_contact: form.preferred_contact,
        notes: form.notes,
        address: myProperty?.address,
        unit_number: myProperty?.unit_number,
      });
      toast.success("The HOA board will reach out shortly.");
      setOpen(false);
      setForm({ ...form, phone: "", notes: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send request");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Card
          data-testid="contact-me-card"
          className="rounded-2xl border-[#D5CFC4] cursor-pointer hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] transition-all duration-300"
        >
          <CardContent className="p-8">
            <div className="w-11 h-11 rounded-full bg-[#EADCC8] flex items-center justify-center mb-4">
              <PhoneCall className="w-5 h-5 text-[#2C4C3B]" />
            </div>
            <p className="font-serif text-xl text-[#1A2421] mb-2">Request a call about monthly payments</p>
            <p className="text-sm text-[#5C6A64] mt-2 mb-6">
              Not sure what plan works best? Let the board know how to reach you and someone will call to walk through your options.
            </p>
            <Button data-testid="contact-me-btn" variant="outline" className="w-full rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white h-11">
              Request contact <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Request Contact</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Phone number</label>
            <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-xl" placeholder="(555) 123-4567" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Preferred contact method</label>
            <Select value={form.preferred_contact} onValueChange={(v) => setForm({ ...form, preferred_contact: v })}>
              <SelectTrigger data-testid="contact-method-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="phone">Phone call</SelectItem>
                <SelectItem value="text">Text message</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Anything the board should know? (optional)</label>
            <Textarea data-testid="contact-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <Button data-testid="contact-submit" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B] h-11">
            {busy ? "Submitting…" : "Send request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentSuccessPage() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [tries, setTries] = useState(0);
  const [autoRedirect, setAutoRedirect] = useState(3); // countdown once paid
  const navigate = useNavigate();

  // Poll every 2s for up to ~60s. Stops once we see paid/failed or the max tries is hit.
  const MAX_TRIES = 30;
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid" || data.payment_status === "failed" || data.payment_status === "canceled" || tries >= MAX_TRIES) return;
        setTimeout(() => setTries((t) => t + 1), 2000);
      } catch { setTimeout(() => setTries((t) => t + 1), 2000); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, tries]);

  const paid = status.payment_status === "paid";
  const failed = status.payment_status === "failed" || status.payment_status === "canceled";
  const timedOut = !paid && !failed && tries >= MAX_TRIES;

  // Auto-redirect to portal 3 s after we confirm paid so the member's dashboard reloads with fresh data.
  useEffect(() => {
    if (!paid) return;
    const t = setInterval(() => setAutoRedirect((n) => (n > 0 ? n - 1 : 0)), 1000);
    const to = setTimeout(() => navigate("/portal?paid=1"), 3000);
    return () => { clearInterval(t); clearTimeout(to); };
  }, [paid, navigate]);

  return (
    <div data-testid="payment-success-page" className="max-w-md mx-auto px-6 py-20 text-center">
      {paid ? (
        <>
          <CheckCircle2 className="w-16 h-16 text-[#2C4C3B] mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-[#1A2421] mb-2">Payment received</h1>
          <p className="text-[#5C6A64] mb-2">{status.mode === "subscription" ? "Your auto-pay is now active." : "Thank you! Your account has been updated."}</p>
          <p className="text-xs text-[#5C6A64]" data-testid="auto-redirect-notice">Taking you back to the portal in {autoRedirect}s…</p>
        </>
      ) : failed ? (
        <>
          <XCircle className="w-16 h-16 text-[#C86650] mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#1A2421] mb-2">Payment didn't go through</h1>
          <p className="text-[#5C6A64] mb-2">Stripe reported the charge as {status.payment_status}. No money was taken from your account.</p>
        </>
      ) : timedOut ? (
        <>
          <Clock className="w-16 h-16 text-[#EADCC8] mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#1A2421] mb-2">Still processing…</h1>
          <p className="text-[#5C6A64] mb-2">Your bank hasn't confirmed the charge yet. This is normal for some card issuers. Refresh your portal in a few minutes — it will show as Paid once Stripe finalizes.</p>
        </>
      ) : (
        <>
          <div className="w-16 h-16 border-4 border-[#2C4C3B] border-t-transparent rounded-full mx-auto animate-spin mb-4" data-testid="payment-spinner" />
          <h1 className="font-serif text-2xl text-[#1A2421]">Confirming payment…</h1>
          <p className="text-xs text-[#5C6A64] mt-2">Talking to Stripe · attempt {tries + 1} of {MAX_TRIES}</p>
        </>
      )}
      <div className="mt-6 flex gap-2 justify-center">
        <Link to="/portal?paid=1"><Button variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B]" data-testid="success-back-btn">Back to Portal</Button></Link>
        <Link to="/portal/history?paid=1"><Button className="rounded-full bg-[#2C4C3B]" data-testid="success-history-btn">View payment history</Button></Link>
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div data-testid="payment-cancel-page" className="max-w-md mx-auto px-6 py-20 text-center">
      <XCircle className="w-16 h-16 text-[#C86650] mx-auto mb-4" />
      <h1 className="font-serif text-3xl text-[#1A2421] mb-2">Payment cancelled</h1>
      <p className="text-[#5C6A64] mb-8">No worries — you can try again when you're ready.</p>
      <Link to="/portal/pay"><Button className="rounded-full bg-[#2C4C3B]" data-testid="cancel-retry-btn">Try again</Button></Link>
    </div>
  );
}
